import { useApi } from '@/api';
import {
  Page,
  ActivityCard,
  PageSkeleton,
  Asset,
  DoubleUpIcon,
  UpIcon,
  DownIcon,
  MultiKpiCard,
  MarketTags,
  Link
} from '@/components';
import {
  Flex,
  Card,
  Box,
  Heading,
  LinkWrapper,
  PercentageValue,
  WalletAddress,
  KpiCard
} from '@blockanalitica/ui';
import { useParams } from 'react-router';
import HistoricTabs from './HistoricTabs';
import HistoricRatesTabs from './HistoricRatesTabs';
import AtRiskTabs from './AtRiskTabs';
import AtRiskKpis from './AtRiskKpis';

type Market = {
  underlying_symbol: string;
  underlying_address: string;
  underlying_name: string;
  total_supply: string;
  total_supply_usd: string;
  total_supply_change: string | null;
  total_supply_change_percentage: string | null;
  total_borrow: string;
  total_borrow_usd: string;
  total_borrow_change: string | null;
  total_borrow_change_percentage: string | null;
  supply_apy: string;
  supply_apy_change: string | null;
  supply_apy_change_percentage: string | null;
  supply_apy_1d: string;
  supply_apy_7d: string;
  supply_apy_30d: string;
  borrow_apy: string;
  borrow_apy_change: string | null;
  borrow_apy_change_percentage: string | null;
  borrow_apy_1d: string;
  borrow_apy_7d: string;
  borrow_apy_30d: string;
  utilization_rate: string;
  utilization_rate_change: string | null;
  utilization_rate_change_percentage: string | null;
  available_liquidity_change_percentage: string | null;
  underlying_price: string;
  underlying_price_change: string | null;
  underlying_price_change_percentage: string | null;
  num_of_borrowers: string;
  num_of_borrowers_change: string | null;
  num_of_borrowers_change_percentage: string | null;
  num_of_suppliers: string;
  num_of_suppliers_change: string | null;
  num_of_suppliers_change_percentage: string | null;
  atoken_address: string;
  variable_debt_token_address: string;
  interest_rate_strategy_address: string;
  total_collateral: string | null;
  status: {
    borrow: string;
    collateral: string;
    supply: string;
    frozen: string;
  };
};
import { ReactNode } from 'react';

export default function MarketPage() {
  const { marketAddress, network } = useParams() as {
    marketAddress: string;
    network: string;
  };
  const [data, error] = useApi<Market>({
    url: `/markets/${marketAddress}/`
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <PageSkeleton />;
  }

  function KpiCardLinkComponent({
    data,
    field,
    to,
    title
  }: {
    data: Market;
    field: string;
    to: string;
    title: ReactNode;
  }) {
    return (
      <>
        {data[field as keyof Market] !== '0' &&
        data[field as keyof Market] !== null ? (
          <LinkWrapper
            to={to}
            // @ts-ignore
            as={Link}
            variant="kpiCardLink">
            <KpiCard
              title={title}
              data={data}
              field={field}
              options={{
                trend: true,
                notation: 'compact',
                linkArrow: true,
                currency: 'USD'
              }}
            />
          </LinkWrapper>
        ) : (
          <KpiCard
            title={title}
            data={data}
            field={field}
            options={{
              notation: 'compact',
              linkArrow: false,
              currency: 'USD',

              valueOptions: {
                currencyValueOptions: {
                  nullable: '-'
                }
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <Page
      title={
        <Asset
          symbol={data.underlying_symbol}
          address={marketAddress}
          network={network}
          iconSize="4"
          fontSize="5"
        />
      }
      actions={
        <Card padding="xs">
          <MarketTags status={data.status} iconSize="3" />
        </Card>
      }>
      <Flex flexDirection={['column', 'row']} gap={['m', 's']}>
        <KpiCard
          title="Total Supplied"
          data={data}
          field="total_supply"
          options={{
            notation: 'compact',
            valueOptions: {
              currencyValueOptions: {
                currency: {
                  name: data.underlying_symbol,
                  address: data.underlying_address,
                  network: network
                }
              }
            },
            trend: true
          }}
          extra={
            <Flex color="muted">
              <DownIcon size="3" />
            </Flex>
          }
        />

        <KpiCard
          title="Total Available"
          data={data}
          field="available_liquidity"
          options={{
            notation: 'compact',
            valueOptions: {
              currencyValueOptions: {
                currency: {
                  name: data.underlying_symbol,
                  address: data.underlying_address,
                  network: network
                }
              }
            },
            trend: true
          }}
          extra={
            <Flex color="muted">
              <DoubleUpIcon size="3" />
            </Flex>
          }
        />
        <KpiCard
          title="Total Borrowed"
          data={data}
          field="total_borrow"
          options={{
            notation: 'compact',
            valueOptions: {
              currencyValueOptions: {
                currency: {
                  name: data.underlying_symbol,
                  address: data.underlying_address,
                  network: network
                }
              }
            },
            trend: true
          }}
          extra={
            <Flex color="muted">
              <UpIcon size="3" />
            </Flex>
          }
        />
      </Flex>
      <HistoricTabs
        underlyingSymbol={data.underlying_symbol}
        underlyingAddress={data.underlying_address}
        network={network}
      />

      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <MultiKpiCard
          title="Supply APY"
          data={data}
          field="supply_apy"
          kpis={[
            {
              title: '1D AVG',
              content: <PercentageValue value={data.supply_apy_1d} />
            },
            {
              title: '7D AVG',
              content: <PercentageValue value={data.supply_apy_7d} />
            },
            {
              title: '30D AVG',
              content: <PercentageValue value={data.supply_apy_30d} />
            }
          ]}
          options={{
            percentage: true,
            variants: {
              valuePercentageValue: 'dynamic'
            }
          }}
        />
        <MultiKpiCard
          title="Borrow APY"
          data={data}
          field="borrow_apy"
          kpis={[
            {
              title: '1D AVG',
              content: <PercentageValue value={data.borrow_apy_1d} />
            },
            {
              title: '7D AVG',
              content: <PercentageValue value={data.borrow_apy_7d} />
            },
            {
              title: '30D AVG',
              content: <PercentageValue value={data.borrow_apy_30d} />
            }
          ]}
          options={{
            percentage: true,
            variants: {
              valuePercentageValue: 'dynamic'
            }
          }}
        />
      </Flex>

      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <Card flexBasis="33%" justifyContent="space-between" gap="s">
          <Flex flexDirection="column" gap="2xs">
            <Heading size="4" paddingBottom="xs">
              {data.underlying_name} Contracts
            </Heading>
            <Flex justifyContent="space-between">
              <Box color="muted">{data.underlying_symbol}</Box>
              <WalletAddress
                address={data.underlying_address}
                options={{
                  inline: true,
                  showBlockie: false,
                  showCopyBtn: true,
                  showEthScanLink: true,
                  network
                }}
              />
            </Flex>
            <Flex justifyContent="space-between">
              <Box color="muted">sp{data.underlying_symbol}</Box>
              <WalletAddress
                address={data.atoken_address}
                options={{
                  inline: true,
                  showBlockie: false,
                  showCopyBtn: true,
                  showEthScanLink: true,
                  network
                }}
              />
            </Flex>
            <Flex justifyContent="space-between">
              <Box color="muted">Variable Debt {data.underlying_symbol}</Box>
              <WalletAddress
                address={data.variable_debt_token_address}
                options={{
                  inline: true,
                  showBlockie: false,
                  showCopyBtn: true,
                  showEthScanLink: true,
                  network
                }}
              />
            </Flex>
            <Flex justifyContent="space-between">
              <Box color="muted">Interest Rate Strategy</Box>
              <WalletAddress
                address={data.interest_rate_strategy_address}
                options={{
                  inline: true,
                  showBlockie: false,
                  showCopyBtn: true,
                  showEthScanLink: true,
                  network
                }}
              />
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap={['s', 'xs']}>
            <KpiCard
              title="Utilization rate"
              data={data}
              field="utilization_rate"
              variant="nested"
              options={{
                percentage: true,
                variants: {
                  valuePercentageValue: 'dynamic'
                }
              }}
            />
            <KpiCard
              title="Oracle price"
              data={data}
              field="underlying_price"
              variant="nested"
              options={{
                currency: 'USD'
              }}
            />
          </Flex>
        </Card>

        <HistoricRatesTabs marketAddress={marketAddress} flexBasis="67%" />
      </Flex>

      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <LinkWrapper
          to="wallets?type=suppliers"
          // @ts-ignore
          as={Link}
          variant="kpiCardLink">
          <KpiCard
            title="N° of suppliers"
            data={data}
            field="num_of_suppliers"
            options={{
              trend: true,
              variants: { valuePercentageValue: 'dynamic' },
              linkArrow: true
            }}
            icon={
              <Flex color="primary">
                <DownIcon size="5" />
              </Flex>
            }
          />
        </LinkWrapper>
        <LinkWrapper
          to="wallets?type=borrowers"
          // @ts-ignore
          as={Link}
          variant="kpiCardLink">
          <KpiCard
            title="N° of borrowers"
            data={data}
            field="num_of_borrowers"
            options={{
              trend: true,
              variants: { valuePercentageValue: 'dynamic' },
              linkArrow: true
            }}
            icon={
              <Flex color="primary">
                <UpIcon size="5" />
              </Flex>
            }
          />
        </LinkWrapper>
      </Flex>
      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <Flex
          flexBasis="33%"
          justifyContent="space-between"
          gap={['m', 's']}
          flexDirection={'column'}>
          <KpiCardLinkComponent
            data={data}
            field="total_debt"
            to="collateral"
            title={`Total debt backed by ${data.underlying_symbol} `}
          />
          <KpiCardLinkComponent
            data={data}
            field="total_collateral"
            to="debt"
            title={`Total collateral backing ${data.underlying_symbol} debt`}
          />
          <AtRiskKpis marketAddress={marketAddress} />{' '}
        </Flex>

        <AtRiskTabs
          marketAddress={marketAddress}
          tag={data.status['collateral']}
          symbol={data.underlying_symbol}
        />
      </Flex>

      <ActivityCard
        url={`/markets/${marketAddress}/heatmap/`}
        linkTo="activity"
        onClickUrl="activity"
      />
    </Page>
  );
}
